import { forwardRef, useState, useCallback, useEffect, useMemo, type CSSProperties } from 'react';
import { HiArrowLeft, HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { FaSave, FaTrash, FaPlus } from 'react-icons/fa';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';

type StepForm = {
  _key: string;
  process_name: string;
  days_to_be_take: number;
  worker_cost_to_be_take: number;
  material_cost_to_be_take: number;
  workers: number[];
  sort_index: number;
};

type StaffInvite = {
  id: number;
  user_id: number;
  user_name: string;
  user_phone_number: string;
  administrative_role_name: string | null;
  skilled_role_names: string[] | null;
  status: string;
  employment_type: string;
};

type ProductionWorker = {
  id: number;
  user_id: number;
  user_name: string;
};

type InitialStep = {
  process_name: string;
  days_to_be_take: number;
  worker_cost_to_be_take: number;
  material_cost_to_be_take: number;
  workers: ProductionWorker[];
  sort_index: number;
};

type WorkFlowModalProps = {
  productId: number;
  productName: string;
  initialSteps: InitialStep[];
  onSaved: () => void;
};

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const sanitizeNum = (value: string | number): number => {
  const str = String(value).replace(/[^0-9.]/g, '');
  const num = Number(str);

  return Number.isFinite(num) ? num : 0;
};

type SortableStepProps = {
  step: StepForm;
  index: number;
  isExpanded: boolean;
  disabled: boolean;
  workersLabel: string;
  onToggleExpand: () => void;
  onDelete: () => void;
  onUpdateStep: (field: keyof StepForm, value: string | number | number[]) => void;
  onOpenWorkerSelector: () => void;
};

const SortableStep = (props: SortableStepProps) => {
  const {
    step,
    index,
    isExpanded,
    disabled,
    workersLabel,
    onToggleExpand,
    onDelete,
    onUpdateStep,
    onOpenWorkerSelector,
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: step._key,
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-gray-200 rounded-xl overflow-hidden bg-white"
    >
      <div
        {...attributes}
        {...listeners}
        className={`w-full flex items-center gap-2 p-4 hover:bg-gray-50 transition-colors select-none ${
          disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
        }`}
      >
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex-1 flex items-center justify-between min-w-0"
        >
          <span className="font-inter font-semibold text-gray-700">
            {index + 1}. {step.process_name || 'Untitled Step'}
          </span>
          {isExpanded ? (
            <HiChevronUp size={20} className="text-gray-500 flex-shrink-0" />
          ) : (
            <HiChevronDown size={20} className="text-gray-500 flex-shrink-0" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100 space-y-4">
          <div>
            <label className="font-inter text-xs font-semibold text-gray-600 uppercase block mb-1">
              Process Name
            </label>
            <input
              type="text"
              value={step.process_name}
              onChange={(e) => onUpdateStep('process_name', e.target.value)}
              placeholder="e.g. Stitching"
              className="w-full h-11 px-3 border border-gray-200 rounded-xl text-sm font-inter outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="font-inter text-xs font-semibold text-gray-600 uppercase block mb-1">
                Days
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={step.days_to_be_take || ''}
                onChange={(e) => onUpdateStep('days_to_be_take', sanitizeNum(e.target.value))}
                className="w-full h-11 px-3 border border-gray-200 rounded-xl text-sm font-inter outline-none"
              />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold text-gray-600 uppercase block mb-1">
                Worker Cost (₹)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={step.worker_cost_to_be_take || ''}
                onChange={(e) =>
                  onUpdateStep('worker_cost_to_be_take', sanitizeNum(e.target.value))
                }
                className="w-full h-11 px-3 border border-gray-200 rounded-xl text-sm font-inter outline-none"
              />
            </div>
            <div>
              <label className="font-inter text-xs font-semibold text-gray-600 uppercase block mb-1">
                Material Cost (₹)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={step.material_cost_to_be_take || ''}
                onChange={(e) =>
                  onUpdateStep('material_cost_to_be_take', sanitizeNum(e.target.value))
                }
                className="w-full h-11 px-3 border border-gray-200 rounded-xl text-sm font-inter outline-none"
              />
            </div>
          </div>

          <div>
            <label className="font-inter text-xs font-semibold text-gray-600 uppercase block mb-1">
              Workers
            </label>
            <div
              onClick={onOpenWorkerSelector}
              className="flex items-center justify-between w-full h-11 px-3 border border-gray-200 rounded-xl text-sm font-inter cursor-pointer hover:border-blue-400 transition-colors bg-white"
            >
              <span className="text-gray-700 truncate">{workersLabel}</span>
              <span className="text-blue-600 text-sm font-medium ml-2 flex-shrink-0">Change</span>
            </div>
            <p className="font-inter text-xs text-gray-500 mt-1">
              Tap to open worker list (multi-select)
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-inter text-sm font-medium"
            >
              <FaTrash size={14} />
              Delete Step
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WorkFlowModal = forwardRef<ModalMethods, WorkFlowModalProps>(
  ({ productId, productName, initialSteps, onSaved }, ref) => {
    const [steps, setSteps] = useState<StepForm[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(0);
    const [staffList, setStaffList] = useState<StaffInvite[]>([]);
    const [isLoadingStaff, setIsLoadingStaff] = useState(false);
    const [workerSelectStepIndex, setWorkerSelectStepIndex] = useState<number | null>(null);
    const [showWorkerModal, setShowWorkerModal] = useState(false);

    const boutique_id = getValueFromLocalStorage('boutique_id');

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    useEffect(() => {
      const formatted: StepForm[] = initialSteps.map((s, idx) => ({
        _key: uid(),
        process_name: s.process_name ?? '',
        days_to_be_take: sanitizeNum(s.days_to_be_take ?? 0),
        worker_cost_to_be_take: sanitizeNum(s.worker_cost_to_be_take ?? 0),
        material_cost_to_be_take: sanitizeNum(s.material_cost_to_be_take ?? 0),
        workers: Array.isArray(s.workers) ? s.workers.map((w) => w.user_id ?? w.id ?? 0) : [],
        sort_index: s.sort_index ?? idx,
      }));

      setSteps(formatted);
      setExpandedStepIndex(formatted.length > 0 ? 0 : null);
    }, [initialSteps]);

    useEffect(() => {
      if (!boutique_id) {
        return;
      }

      const fetchStaff = async () => {
        setIsLoadingStaff(true);

        try {
          const response = await api.getRequest('staff/invites');

          if (response.status && response.data?.staff_invite_list) {
            setStaffList(response.data.staff_invite_list);
          } else {
            setStaffList([]);
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'staff-fetch-error');
          }
          setStaffList([]);
        } finally {
          setIsLoadingStaff(false);
        }
      };

      void fetchStaff();
    }, [boutique_id]);

    const workerIdToName = useMemo(() => {
      const map: Record<number, string> = {};

      staffList.forEach((s) => {
        map[s.user_id] = s.user_name ?? '';
      });

      return map;
    }, [staffList]);

    const handleSave = useCallback(async () => {
      const validSteps = steps.filter((s) => (s.process_name ?? '').trim() !== '');

      if (validSteps.length === 0) {
        toasts('error', 'Add at least one step with a process name', 'workflow-save-empty');

        return;
      }

      setIsSaving(true);

      try {
        const boutique_id = getValueFromLocalStorage('boutique_id');
        const payload = {
          boutique_id,
          readymade_outfit_id: productId,
          steps: validSteps.map((s, idx) => ({
            process_name: s.process_name.trim(),
            days_to_be_take: s.days_to_be_take,
            worker_cost_to_be_take: s.worker_cost_to_be_take,
            material_cost_to_be_take: s.material_cost_to_be_take,
            worker_ids: s.workers,
            sort_index: idx + 1,
          })),
        };

        const response = await api.postRequest('production_process/save', payload);

        if (response.status) {
          toasts('success', 'Work flow saved successfully', 'workflow-save-success');
          onSaved();

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to save work flow', 'workflow-save-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'workflow-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    }, [steps, productId, onSaved, ref]);

    const handleAddStep = () => {
      setSteps((prev) => [
        ...prev,
        {
          _key: uid(),
          process_name: '',
          days_to_be_take: 0,
          worker_cost_to_be_take: 0,
          material_cost_to_be_take: 0,
          workers: [],
          sort_index: steps.length,
        },
      ]);
      setExpandedStepIndex(steps.length);
    };

    const handleDeleteStep = (index: number) => {
      setSteps((prev) => prev.filter((_, i) => i !== index));
      setExpandedStepIndex((prev) => {
        if (prev === null) {
          return null;
        }

        if (prev === index) {
          return index > 0 ? index - 1 : 0;
        }

        if (prev > index) {
          return prev - 1;
        }

        return prev;
      });
    };

    const handleUpdateStep = (
      index: number,
      field: keyof StepForm,
      value: string | number | number[]
    ) => {
      setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    };

    const openWorkerSelector = (stepIndex: number) => {
      setWorkerSelectStepIndex(stepIndex);
      setShowWorkerModal(true);
    };

    const closeWorkerSelector = () => {
      setWorkerSelectStepIndex(null);
      setShowWorkerModal(false);
    };

    const handleWorkerSelect = (selectedIds: number[]) => {
      if (workerSelectStepIndex !== null) {
        handleUpdateStep(workerSelectStepIndex, 'workers', selectedIds);
      }
      closeWorkerSelector();
    };

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      setSteps((prev) => {
        const oldIndex = prev.findIndex((s) => s._key === active.id);
        const newIndex = prev.findIndex((s) => s._key === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return prev;
        }

        return arrayMove(prev, oldIndex, newIndex).map((s, idx) => ({
          ...s,
          sort_index: idx,
        }));
      });
    }, []);

    const handleBack = useCallback(() => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    }, [ref]);

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <HiArrowLeft size={18} />
          <Text size="small" color="black" className="font-inter">
            Back
          </Text>
        </button>
        <Text size="large" fontWeight={700} className="font-inter">
          Work Flow - {productName}
        </Text>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || steps.every((s) => !(s.process_name ?? '').trim())}
          className="px-4 py-2 bg-[#4D7AFF] text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader />
              <Text size="small" color="white" className="font-inter font-medium">
                Saving...
              </Text>
            </>
          ) : (
            <>
              <FaSave size={16} />
              <Text size="small" color="white" className="font-inter font-medium">
                Save
              </Text>
            </>
          )}
        </button>
      </div>
    );

    return (
      <>
        <Modal
          ref={ref}
          size="xl"
          headerComponent={headerComponent}
          showFooter={false}
          showCloseIcon={false}
        >
          <Text className="font-inter text-sm text-gray-600 mt-1 block" color="black">
            Add time, per-piece worker cost, material cost and required roles for each step to
            estimate total effort.
          </Text>

          <div className="mt-6 space-y-3 pb-5 ">
            {steps.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map((s) => s._key)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {steps.map((step, index) => {
                      const isExpanded = expandedStepIndex === index;
                      const workerNames = step.workers
                        .map((userId) => workerIdToName[userId] || `ID ${userId}`)
                        .filter(Boolean);
                      const workersLabel =
                        workerNames.length > 3
                          ? `${workerNames.slice(0, 3).join(', ')} +${workerNames.length - 3} more`
                          : workerNames.join(', ') || 'None selected';

                      return (
                        <SortableStep
                          key={step._key}
                          step={step}
                          index={index}
                          isExpanded={isExpanded}
                          disabled={isSaving}
                          workersLabel={workersLabel}
                          onToggleExpand={() =>
                            setExpandedStepIndex((prev) => (prev === index ? null : index))
                          }
                          onDelete={() => handleDeleteStep(index)}
                          onUpdateStep={(field, value) => handleUpdateStep(index, field, value)}
                          onOpenWorkerSelector={() => openWorkerSelector(index)}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            ) : null}

            <button
              type="button"
              onClick={handleAddStep}
              className="w-full py-4 border-2 border-dashed border-blue-400 text-blue-600 rounded-xl font-inter text-sm font-semibold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors"
            >
              <FaPlus size={16} />
              Add Step
            </button>
          </div>
        </Modal>

        {showWorkerModal && workerSelectStepIndex !== null && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div
              className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <Text size="medium" fontWeight={600} className="font-inter">
                  Select Workers
                </Text>
                <button
                  type="button"
                  onClick={closeWorkerSelector}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingStaff ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader />
                    <Text className="font-inter text-sm ml-2" color="black">
                      Loading workers...
                    </Text>
                  </div>
                ) : staffList.length === 0 ? (
                  <Text className="font-inter text-sm text-gray-500" color="black">
                    No workers found
                  </Text>
                ) : (
                  <div className="space-y-2">
                    {staffList.map((staff) => {
                      const isChecked = (steps[workerSelectStepIndex]?.workers ?? []).includes(
                        staff.user_id
                      );

                      return (
                        <label
                          key={staff.id}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const current = steps[workerSelectStepIndex]?.workers ?? [];
                              const updated = current.includes(staff.user_id)
                                ? current.filter((x) => x !== staff.user_id)
                                : [...current, staff.user_id];

                              handleUpdateStep(workerSelectStepIndex, 'workers', updated);
                            }}
                            className="h-4 w-4 accent-[#4D7AFF]"
                          />
                          <div className="flex-1 min-w-0">
                            <Text className="font-inter text-sm font-medium truncate" color="black">
                              {staff.user_name}
                            </Text>
                            {(staff.skilled_role_names?.length ||
                              staff.administrative_role_name) && (
                              <Text className="font-inter text-xs text-gray-500" color="black">
                                {[
                                  ...(staff.skilled_role_names ?? []),
                                  staff.administrative_role_name,
                                ]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </Text>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeWorkerSelector}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-inter text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleWorkerSelect(steps[workerSelectStepIndex]?.workers ?? [])}
                  className="px-4 py-2 bg-[#4D7AFF] text-white rounded-lg font-inter text-sm font-medium hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

WorkFlowModal.displayName = 'WorkFlowModal';

export default WorkFlowModal;
